import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { BidRoundService } from "../../../services/bid-round.service";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmDialogComponent } from "../../../shared/confirm-dialog/confirm-dialog.component";
import { BaseComponent } from "src/app/shared/base.component";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-bid-round-management",
  templateUrl: "./bid-round-management.component.html",
  styleUrls: ["./bid-round-management.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BidRoundManagementComponent extends BaseComponent implements OnInit {
  bidRoundForm: FormGroup;
  searchForm: FormGroup;
  bidStatusOptions = ['OPEN', 'CREATED', 'CLOSED'];
  bidTypeOptions = ['Monthly', 'Day ahead'];
  isEdit = false;
  bidRoundId: string | null = null;
  bidRounds: any[] = [];
  errorMessage = '';
  showForm = false;
  minDate: string;

  constructor(
    private fb: FormBuilder,
    private bidRoundService: BidRoundService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {
    super();
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    this.minDate = yesterday.toISOString().slice(0, 16);

    this.bidRoundForm = this.fb.group({
      start_time: ["", Validators.required],
      end_time: ["", [Validators.required]],
      bid_type: ["", Validators.required],
      start_trade_period: ["", [Validators.required]],
      end_trade_period: ["", [Validators.required]],
      status: [""],
    });

    this.searchForm = this.fb.group({
      bid_status: ["OPEN"]
    });
  }

  ngOnInit(): void {
    this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.bidRoundId = params['id'];
      if (this.bidRoundId) {
        this.isEdit = true;
        this.loadBidRound(this.bidRoundId);
        this.showForm = true;
      }
      this.cdr.markForCheck();
    });
    this.onSearch();
  }

  loadBidRound(id: string): void {
    this.bidRoundService.getBidRound(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.bidRoundForm.patchValue(data);
        this.cdr.markForCheck();
      });
  }

  onSubmit(): void {
    if (this.bidRoundForm.invalid) return;

    const formData = { ...this.bidRoundForm.value };
    ['start_time', 'end_time', 'start_trade_period', 'end_trade_period'].forEach(field => {
      if (formData[field]) {
        formData[field] = this.formatDateForBackend(new Date(formData[field]));
      }
    });

    const request = (this.isEdit && this.bidRoundId) 
      ? this.bidRoundService.updateBidRound(this.bidRoundId, formData)
      : this.bidRoundService.createBidRound(formData);

    request.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.onSearch();
        this.showForm = false;
      },
      error: (err) => console.error("Error saving bid round:", err)
    });
  }

  private formatDateForBackend(date: Date): string {
    return date.getFullYear().toString().padStart(4, '0') + '-' +
      (date.getMonth() + 1).toString().padStart(2, '0') + '-' +
      date.getDate().toString().padStart(2, '0') + ' ' +
      date.getHours().toString().padStart(2, '0') + ':' +
      date.getMinutes().toString().padStart(2, '0');
  }

  onDelete(bidRoundId: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'custom-dialog-container',
      data: {
        title: 'Confirmation',
        message: 'Are you sure you want to delete this bid round?'
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result) {
        this.bidRoundService.deleteBidRound(bidRoundId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => this.onSearch(),
            error: (err) => console.error("Error deleting bid round:", err)
          });
      }
    });
  }

  onCancel(): void {
    this.showForm = false;
    this.cdr.markForCheck();
  }

  onSearch(): void {
    const filters = this.searchForm.value;
    this.bidRoundService.searchBidRounds(filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.bidRounds = data.items;
        this.showForm = false;
        this.cdr.markForCheck();
      });
  }

  onEdit(bidRound: any): void {
    this.bidRoundId = bidRound.id;
    this.isEdit = true;
    this.showForm = true;
    this.bidRoundForm.patchValue(bidRound);
    this.cdr.markForCheck();
  }

  onNew(): void {
    this.isEdit = false;
    this.showForm = true;
    this.bidRoundForm.reset();
    this.prepopulateDates();
    this.bidRoundForm.patchValue({ bid_type: 'Day ahead' });
    this.cdr.markForCheck();
  }

  private prepopulateDates(): void {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const start_time = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 0, 0);
    const end_time = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 18, 0);
    const start_trade_period = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate() + 1, 0, 0);
    const end_trade_period = new Date(start_trade_period.getFullYear(), start_trade_period.getMonth(), start_trade_period.getDate() + 1);

    this.bidRoundForm.patchValue({
      start_time: this.formatDateForInput(start_time),
      end_time: this.formatDateForInput(end_time),
      start_trade_period: this.formatDateForInput(start_trade_period),
      end_trade_period: this.formatDateForInput(end_trade_period),
    });
  }

  private formatDateForInput(date: Date): string {
    return date.getFullYear().toString().padStart(4, '0') + '-' +
      (date.getMonth() + 1).toString().padStart(2, '0') + '-' +
      date.getDate().toString().padStart(2, '0') + 'T' +
      date.getHours().toString().padStart(2, '0') + ':' +
      date.getMinutes().toString().padStart(2, '0');
  }
}
